import type { ThemeConfig } from "@/types/theme";

export const tailwindIndigo: ThemeConfig = {
  classes: {
    // Color classes
    primary: "text-indigo-700 dark:text-indigo-400",
    secondary: "text-indigo-900 dark:text-indigo-500",
    accent: "text-violet-600 dark:text-violet-400",

    // Background classes
    backgroundPrimary: "bg-white dark:bg-gray-950",
    backgroundSecondary: "bg-gray-50 dark:bg-gray-900",
    backgroundSurface: "bg-gray-50 dark:bg-gray-800",
    backgroundTertiary: "bg-indigo-50/60 dark:bg-gray-800",
    backgroundAccent: "bg-indigo-600 dark:bg-indigo-700",
    backgroundPrimaryHover: "hover:bg-gray-50 dark:hover:bg-gray-900",
    decorativeBackground:
      "bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-gray-900 dark:to-gray-950",

    // Shared alternates (same across palettes)
    backgroundSoft: "bg-gray-50 dark:bg-gray-900",
    backgroundLighter: "bg-white dark:bg-gray-900",

    backgroundPattern: "bg-indigo-100 dark:bg-gray-800",
    backgroundPrimaryColor: "bg-indigo-600 dark:bg-indigo-500",
    backgroundSecondaryColor: "bg-indigo-800 dark:bg-indigo-700",
    backgroundAccentColor: "bg-violet-500 dark:bg-violet-400",
    backgroundPrimarySubtle: "bg-indigo-50 dark:bg-indigo-950",

    // Card backgrounds
    backgroundCard: "bg-white dark:bg-gray-950",
    backgroundElevated: "bg-white dark:bg-gray-800",
    backgroundHighlighted:
      "bg-gradient-to-br from-white to-indigo-50 dark:from-gray-800 dark:to-gray-900",

    // Text classes
    textPrimary: "text-gray-800 dark:text-gray-100",
    textSecondary: "text-gray-600 dark:text-gray-300",
    textMuted: "text-gray-500 dark:text-gray-400",
    textInverse: "text-white dark:text-gray-900",
    textAccent: "text-indigo-700 dark:text-indigo-400",
    textOnAccent: "text-white",

    // Text on dark backgrounds
    textPrimaryOnDark: "text-gray-100",
    textSecondaryOnDark: "text-gray-300",
    textMutedOnDark: "text-gray-400",

    // Icon classes
    iconText: "text-indigo-600 dark:text-indigo-400",
    iconBackgroundFilled: "bg-indigo-100 dark:bg-indigo-950",
    iconBackgroundGradient: "bg-gradient-to-br from-indigo-500 to-violet-500",
    iconBackgroundDefault: "bg-indigo-50 dark:bg-indigo-950/50",
    iconBorder: "border-indigo-200 dark:border-indigo-800",

    // Border classes
    borderDefault: "border-indigo-200 dark:border-gray-700",
    borderLight: "border-indigo-100 dark:border-gray-800",
    borderStrong: "border-indigo-300 dark:border-gray-600",
    borderPrimary: "border-indigo-700 dark:border-indigo-500",
    borderSecondary: "border-indigo-900 dark:border-indigo-700",
    borderBottom: "border-b border-indigo-200 dark:border-gray-700",
    borderAccent: "border-indigo-600 dark:border-indigo-400",

    // Badge classes
    badgeBackground: "bg-indigo-100 dark:bg-indigo-950",
    badgeText: "text-indigo-700 dark:text-indigo-300",

    // Shadow classes
    shadowPrimary: "shadow-indigo-500/20",
    shadowDefault: "shadow-sm",
    shadowHover: "hover:shadow-lg hover:shadow-indigo-500/10",
    shadowSmall: "shadow-sm",
    shadowMedium: "shadow-md",
    shadowLarge: "shadow-lg",
    shadowXLarge: "shadow-xl",
    shadowGlow: "shadow-[0_0_30px_rgba(99,102,241,0.30)]", // indigo-500 glow
    shadowInset: "shadow-inner",

    // Interactive states
    hoverBackground: "hover:bg-indigo-50 dark:hover:bg-gray-800",
    activeBackground: "active:bg-indigo-100 dark:active:bg-gray-700",
    accentPrimary:
      "bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-700 dark:hover:bg-indigo-600",

    // Creative Bold Hero classes
    backgroundBold: "bg-indigo-400 dark:bg-indigo-600",
    textEmphasis: "text-amber-600 dark:text-amber-400",

    // Gradient classes
    gradientPrimary: "bg-gradient-to-r from-indigo-600 to-indigo-700",
    gradientSecondary: "bg-gradient-to-br from-indigo-700 to-indigo-900",
    gradientAccent: "bg-gradient-to-r from-indigo-500 to-violet-500",
    gradientSubtle:
      "bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-gray-900 dark:to-gray-950",
    gradientVibrant:
      "bg-gradient-to-r from-indigo-600 via-violet-500 to-indigo-600",
    gradientHero:
      "bg-gradient-to-br from-indigo-600 via-violet-500 to-indigo-700",

    // Border radius classes
    roundedSmall: "rounded",
    roundedMedium: "rounded-md",
    roundedLarge: "rounded-lg",
    roundedXLarge: "rounded-xl",
    roundedFull: "rounded-full",

    // Opacity classes
    opacityDisabled: "opacity-50",
    opacityHover: "opacity-80",
    opacityBackdrop: "opacity-60",
    opacitySubtle: "opacity-30",

    // Transition classes
    transitionFast: "transition-all duration-150",
    transitionNormal: "transition-all duration-300",
    transitionSlow: "transition-all duration-500",

    // Z-index classes
    zDropdown: "z-10",
    zModal: "z-50",
    zOverlay: "z-40",
    zTooltip: "z-[60]",
    zFixed: "z-30",

    // Status colors (kept consistent across palettes)
    error: "text-red-600 dark:text-red-400",
    success: "text-green-600 dark:text-green-400",
    warning: "text-amber-600 dark:text-amber-400",
    info: "text-blue-600 dark:text-blue-400",

    errorBg: "bg-red-100 dark:bg-red-950",
    successBg: "bg-green-100 dark:bg-green-950",
    warningBg: "bg-amber-100 dark:bg-amber-950",
    infoBg: "bg-blue-100 dark:bg-blue-950",
  },
};
